/**
 * <sc-cart-drawer-mobile>
 *
 * Mobile-only bottom cart drawer. Triggered by Add-to-Cart on PDP and by
 * tapping the header cart icon on mobile. Desktop keeps the legacy
 * right-side cart-icon drawer untouched.
 *
 * Public API (called from product-form.js and global.js cart-icon):
 *   - window.openCartDrawer(triggerEl?)  — convenience global
 *   - drawer.open(triggerEl?)            — slide up; no-op on desktop
 *   - drawer.close()                     — slide down; restore focus
 *
 * Pub-sub:
 *   - Subscribes to PUB_SUB_EVENTS.cartUpdate (defensive only; the section
 *     swap already re-runs connectedCallback).
 *   - Publishes PUB_SUB_EVENTS.cartUpdate after a successful remove so the
 *     header cart-icon (and anything else listening) refreshes.
 *
 * Section swap (add-to-cart only):
 *   - Agent 1's add-to-cart flow POSTs /cart/add.js with
 *     sections=sc-cart-drawer-mobile and writes the response into
 *     #shopify-section-sc-cart-drawer-mobile.innerHTML.
 *   - When that happens this element is destroyed and a new one is parsed —
 *     connectedCallback on the new instance re-binds everything.
 *
 * Remove flow (own handler, surgical — no section swap):
 *   - Optimistically removes the <li> from the DOM so the user sees no
 *     delay. Fires /cart/change.js (still with sections=) in the
 *     background, then patches ONLY the subtotal text and (if the cart is
 *     now empty) the items area + footer. Avoids tearing down the custom
 *     element, which prevented the visible flicker caused by the prior
 *     full-section swap (scroll-lock release + Elfsight reappear + panel
 *     redraw all on every remove).
 */
(function () {
  const TAG = 'sc-cart-drawer-mobile';
  const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])';

  class ScCartDrawerMobile extends HTMLElement {
    constructor() {
      super();
      this._panel = null;
      this._lastFocused = null;
      this._cartUpdateUnsub = null;
      this._removing = new Set();
      this._hiddenChatEls = [];
      this._onClick = this._onClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onCartUpdate = this._onCartUpdate.bind(this);
    }

    connectedCallback() {
      this._panel = this.querySelector('.sc-cart-drawer-mobile__panel');
      this.addEventListener('click', this._onClick);
      document.addEventListener('keydown', this._onKeydown);

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        this._cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, this._onCartUpdate);
      }

      // After a section-swap (Agent 1's add-to-cart round-trip) a new drawer
      // instance is parsed with placeholder [data-pickup-location-variant-id]
      // elements that need fetching. sc-pickup-location.js exposes a
      // re-entrant entry point — call it here so the new items get populated
      // without waiting for the next cartUpdate fan-out.
      if (typeof window.scPopulatePickupLocations === 'function') {
        window.scPopulatePickupLocations(this);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      document.removeEventListener('keydown', this._onKeydown);
      if (this._cartUpdateUnsub) {
        this._cartUpdateUnsub();
        this._cartUpdateUnsub = null;
      }
      // If we're being unmounted while open (e.g. AJAX swap), unlock the body
      // and restore the chat widget. The new instance will re-lock + re-hide
      // when it opens. A one-frame flicker is acceptable on remove-item.
      this._unlockScroll();
      this._restoreChatWidget();
    }

    // ---- Public API ----

    open(triggerEl) {
      this._lastFocused =
        triggerEl && typeof triggerEl.focus === 'function' ? triggerEl : document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      this._lockScroll();
      this._hideChatWidget();
      if (typeof window.scPopulatePickupLocations === 'function') {
        window.scPopulatePickupLocations(this);
      }
      // Move focus inside the panel on the next frame so the open transition
      // has started before the screen reader announces the dialog.
      requestAnimationFrame(() => {
        const closeBtn = this.querySelector('.sc-cart-drawer-mobile__close');
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this._unlockScroll();
      this._restoreChatWidget();
      const last = this._lastFocused;
      if (last && typeof last.focus === 'function' && document.body.contains(last)) {
        try {
          last.focus({ preventScroll: true });
        } catch (_) {
          /* swallow */
        }
      }
    }

    // ---- Internals ----

    _lockScroll() {
      document.documentElement.classList.add('sc-cart-drawer-mobile-locked');
    }

    _unlockScroll() {
      document.documentElement.classList.remove('sc-cart-drawer-mobile-locked');
    }

    // Chat-widget hide/restore. The actually-visible bottom-right chat bubble
    // on this store is Shopify Inbox (not Elfsight — the elfsight-app-1456b500
    // container exists in the static HTML but the widget throws
    // WIDGET_NOT_FOUND at init and never renders). Selectors below target
    // Inbox first; Elfsight + legacy ES classes are kept as defensive fallback
    // for other stores / future widgets. setProperty(..., 'important') beats
    // !important rules in third-party CDN stylesheets.
    _hideChatWidget() {
      const selectors = [
        // Shopify Inbox — actual visible chat bubble
        '[class*="chat-button-container"]',
        'iframe[src*="cdn.shopify.com/extensions"]',
        'iframe[src*="shopify-inbox"]',
        'iframe[src*="shop.app/chat"]',
        '[id*="shopify-chat"]',
        '[id*="ShopifyChat"]',
        // Elfsight + legacy (defensive fallback)
        '.elfsight-app-1456b500-0ab1-4458-8d43-fa7e0c8875f8',
        '[class*="eapps-"]',
        '[class*="es-widget"]',
        '[class*="es-app"]',
      ];
      this._hiddenChatEls = [];
      const matched = {};
      selectors.forEach((sel) => {
        const els = document.querySelectorAll(sel);
        if (els.length) matched[sel] = els.length;
        els.forEach((el) => {
          this._hiddenChatEls.push({
            el,
            prevDisplay: el.style.getPropertyValue('display'),
            prevPriority: el.style.getPropertyPriority('display'),
          });
          el.style.setProperty('display', 'none', 'important');
        });
      });
      // eslint-disable-next-line no-console
      console.log(
        '[sc-cart-drawer-mobile] hide chat — selectors matched:',
        matched,
        'total hidden:',
        this._hiddenChatEls.length
      );
      // BONUS: enumerate any fixed-position element in the bottom-right region
      // of the viewport. If the chat bubble is still visible after this runs,
      // the answer should be in this list — add its selector above and the
      // next open() will hide it.
      const candidates = Array.from(document.body.querySelectorAll('*'))
        .filter((el) => {
          const cs = getComputedStyle(el);
          if (cs.position !== 'fixed') return false;
          const r = el.getBoundingClientRect();
          return (
            r.right >= window.innerWidth - 200 &&
            r.bottom >= window.innerHeight - 200 &&
            r.width < 200 &&
            r.height < 200
          );
        })
        .slice(0, 5)
        .map((el) => ({
          tag: el.tagName,
          id: el.id,
          classes: el.className,
          src: el.src || null,
        }));
      if (candidates.length) {
        // eslint-disable-next-line no-console
        console.log(
          '[sc-cart-drawer-mobile] fixed bottom-right candidates (NOT necessarily hidden):',
          candidates
        );
      }
    }

    _restoreChatWidget() {
      this._hiddenChatEls.forEach(({ el, prevDisplay, prevPriority }) => {
        if (prevDisplay) {
          el.style.setProperty('display', prevDisplay, prevPriority || '');
        } else {
          el.style.removeProperty('display');
        }
      });
      this._hiddenChatEls = [];
    }

    _onClick(event) {
      const closeEl = event.target.closest('[data-action="close"]');
      if (closeEl && this.contains(closeEl)) {
        event.preventDefault();
        this.close();
        return;
      }
      const removeBtn = event.target.closest('[data-action="remove"]');
      if (removeBtn && this.contains(removeBtn)) {
        event.preventDefault();
        this._handleRemove(removeBtn);
      }
    }

    _onKeydown(event) {
      if (!this.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'Tab') {
        this._trapTab(event);
      }
    }

    _trapTab(event) {
      if (!this._panel) return;
      const focusables = Array.from(this._panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !this._panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    _handleRemove(button) {
      const lineKey = button.dataset.lineKey;
      if (!lineKey || this._removing.has(lineKey)) return;
      this._removing.add(lineKey);
      button.disabled = true;

      const row = button.closest('.sc-cart-drawer-mobile__item');
      const parent = row ? row.parentNode : null;
      const nextSibling = row ? row.nextSibling : null;

      // Optimistic remove: take the <li> out immediately. No flicker — the
      // surrounding panel, scroll lock, and chat-widget state all stay put.
      if (row) row.remove();
      // Hand keyboard focus somewhere sensible before the browser drops it
      // on <body> (which would Tab the user right out of the drawer).
      this._moveFocusAfterRemove();

      const url = `${window.routes.cart_change_url}?sections=sc-cart-drawer-mobile`;
      const config =
        typeof window.fetchConfig === 'function'
          ? window.fetchConfig('json')
          : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

      // `sections` must be in the POST body — the cart AJAX endpoints ignore
      // the query-param form, which left _applyPostRemoveUpdate's subtotal
      // patch silently no-oping.
      fetch(url, { ...config, body: JSON.stringify({ id: lineKey, quantity: 0, sections: TAG }) })
        .then((response) => {
          if (!response.ok) throw new Error(`cart/change responded ${response.status}`);
          return response.json();
        })
        .then((data) => {
          // Server-state verification — Shopify can return 200 OK while
          // leaving the line in cart.items (concurrent write, inventory hold,
          // cart-extension hook). Treat that as a remove failure: roll back
          // the DOM, surface an error, do NOT publish cartUpdate.
          if (data && Array.isArray(data.items) && data.items.some((it) => it && it.key === lineKey)) {
            const err = new Error('cart/change 200 but lineKey still present in items');
            err.serverPresent = true;
            throw err;
          }
          this._applyPostRemoveUpdate(data);
          if (
            typeof publish === 'function' &&
            typeof PUB_SUB_EVENTS !== 'undefined' &&
            PUB_SUB_EVENTS.cartUpdate
          ) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: TAG,
              cartData: data,
            });
          }
        })
        .catch((err) => {
          console.error('[sc-cart-drawer-mobile] remove failed:', err);
          // Rollback: put the row back where it was so the user can retry.
          if (row && parent && document.body.contains(parent)) {
            if (nextSibling && nextSibling.parentNode === parent) {
              parent.insertBefore(row, nextSibling);
            } else {
              parent.appendChild(row);
            }
          }
          button.disabled = false;
          this._showRemoveError();
        })
        .finally(() => {
          this._removing.delete(lineKey);
        });
    }

    // Surgical patch — update only what changed; don't replace the section.
    _applyPostRemoveUpdate(data) {
      if (!data || !data.sections || !data.sections[TAG]) return;

      const parsed = new DOMParser().parseFromString(data.sections[TAG], 'text/html');

      // Subtotal text — pull the freshly-formatted money from the new
      // Liquid render so we don't have to ship a JS money formatter.
      const newSubtotal = parsed.querySelector('.sc-cart-drawer-mobile__subtotal-value');
      const currentSubtotal = this.querySelector('.sc-cart-drawer-mobile__subtotal-value');
      if (newSubtotal && currentSubtotal) {
        currentSubtotal.textContent = newSubtotal.textContent;
      }

      // If the cart is now empty, swap the items area to the empty state
      // and drop the footer entirely. Stay open (per spec).
      if (typeof data.item_count === 'number' && data.item_count === 0) {
        const newItemsArea = parsed.querySelector('.sc-cart-drawer-mobile__items');
        const currentItemsArea = this.querySelector('.sc-cart-drawer-mobile__items');
        if (newItemsArea && currentItemsArea) {
          currentItemsArea.innerHTML = newItemsArea.innerHTML;
        }
        const footer = this.querySelector('.sc-cart-drawer-mobile__footer');
        if (footer) footer.remove();
        // Refresh the data-item-count for any code that reads it.
        this.setAttribute('data-item-count', '0');
      } else if (typeof data.item_count === 'number') {
        this.setAttribute('data-item-count', String(data.item_count));
      }
    }

    _moveFocusAfterRemove() {
      const nextRemove = this.querySelector('[data-action="remove"]:not([disabled])');
      const fallback = this.querySelector('.sc-cart-drawer-mobile__close');
      const target = nextRemove || fallback;
      if (target && typeof target.focus === 'function') {
        try {
          target.focus({ preventScroll: true });
        } catch (_) {
          /* swallow */
        }
      }
    }

    _showRemoveError() {
      const itemsArea = this.querySelector('.sc-cart-drawer-mobile__items');
      if (!itemsArea) return;
      const errorEl = document.createElement('p');
      errorEl.className = 'sc-cart-drawer-mobile__error';
      errorEl.setAttribute('role', 'alert');
      errorEl.style.cssText =
        'color:#c00;font-size:12px;padding:8px 12px;margin:8px 0;background:#fdecea;border:1px solid #f5c2c0;border-radius:4px;';
      errorEl.textContent = 'Could not remove item. Please try again.';
      itemsArea.prepend(errorEl);
      setTimeout(() => errorEl.remove(), 4000);
    }

    _onCartUpdate(event) {
      // Add-to-cart already swaps #shopify-section-sc-cart-drawer-mobile
      // wholesale, and our own remove handler patches in place (and sets
      // source: TAG, which we skip here). This handles pushes from OTHER
      // surfaces — e.g. the /cart page's quantity stepper and remove button
      // (sc-cart-page.js), which bundle our section HTML into their
      // /cart/change.js response so the drawer's rows ("× N"), subtotal,
      // and footer stay in sync without a page refresh.
      if (!event || event.source === TAG) return;
      const data = event.cartData;
      if (!data || !data.sections || !data.sections[TAG]) return;

      const parsed = new DOMParser().parseFromString(data.sections[TAG], 'text/html');

      const newItemsArea = parsed.querySelector('.sc-cart-drawer-mobile__items');
      const currentItemsArea = this.querySelector('.sc-cart-drawer-mobile__items');
      if (newItemsArea && currentItemsArea) {
        currentItemsArea.innerHTML = newItemsArea.innerHTML;
      }

      const newSubtotal = parsed.querySelector('.sc-cart-drawer-mobile__subtotal-value');
      const currentSubtotal = this.querySelector('.sc-cart-drawer-mobile__subtotal-value');
      if (newSubtotal && currentSubtotal) {
        currentSubtotal.textContent = newSubtotal.textContent;
      }

      if (typeof data.item_count === 'number') {
        this.setAttribute('data-item-count', String(data.item_count));
        const currentFooter = this.querySelector('.sc-cart-drawer-mobile__footer');
        if (data.item_count === 0) {
          if (currentFooter) currentFooter.remove();
        } else if (!currentFooter) {
          // Cart went empty earlier in this session and now has items again.
          const newFooter = parsed.querySelector('.sc-cart-drawer-mobile__footer');
          if (newFooter && this._panel) this._panel.appendChild(newFooter);
        }
      }

      // Freshly-parsed rows carry unpopulated pickup-location placeholders.
      if (typeof window.scPopulatePickupLocations === 'function') {
        window.scPopulatePickupLocations(this);
      }
    }
  }

  if (!customElements.get(TAG)) {
    customElements.define(TAG, ScCartDrawerMobile);
  }

  // Convenience global for product-form.js / cart-icon to invoke without
  // having to query the DOM each time.
  window.openCartDrawer = function (triggerEl) {
    const drawer = document.querySelector(TAG);
    if (drawer && typeof drawer.open === 'function') drawer.open(triggerEl);
  };
})();
